import React, { useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import './OrderPFAS.css';
import {
  getProduct,
  getProductPrice,
  createCart,
  getCheckoutUrl,
  updateCartQuantity,
  updateCartBuyer,
  updateApiCart,
  getExternalOrder,
  handlePlaceOrder,
  getCart,
  updateApiCartWithCallbacks,
} from './shop/utils.js';
import useApiCart from './shop/useApiCart.js';
import CheckoutPaymentMethod from './shop/CheckoutPaymentMethod';
import OrderThankYouPage from './shop/OrderThankYouPage';
import { DEFAULT_PRODUCT_HANDLE } from './constants/shop.js';
import AddressCheckoutForm from './shop/AddressCheckoutForm.js';
import OrderSubjectsForm from './shop/OrderSubjectsForm';
import OrderCheckoutFirstStep from './shop/OrderCheckoutFirstStep';
import OrderCheckoutStepperWrapper from './shop/OrderCheckoutStepperWrapper';
import PrimaryBlueButton from './components/common/buttons/PrimaryBlueButton';
import OrderCheckoutAddressContainer from './shop/OrderCheckoutAddressContainer';
import CheckoutPaymentMethodContainer from './shop/CheckoutPaymentMethodContainer';
import { useThemeContext } from './contexts/BaseThemeContextProvider.js';
import { CHECKOUT_CART_DATA_KEY, CHECKOUT_CART_KEY } from './constants/localStorageKeys';

export default function OrderPFAS() {
  const [activeStep, setActiveStep] = React.useState(0);
  const [popupMessage, setPopupMessage] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [productPrice, setProductPrice] = useState();
  const [order, setOrder] = useState('');
  const [product, setProduct] = useState(null);
  const [sCheckoutURL, setSCheckoutURL] = useState(null);
  const [goToShippingEnabled, setGoToShippingEnabled] = useState(false);
  const [goToShippingSubmitting, setGoToShippingSubmitting] = useState(false);
  const [goToCheckoutEnabled, setGoToCheckoutEnabled] = useState(false);
  const [goToCheckoutSubmitting, setGoToCheckoutSubmitting] = useState(false);
  const { theme, cognitoUserInfo } = useThemeContext();
  const [cart, setCart] = useState(localStorage.getItem(CHECKOUT_CART_KEY));
  const { apiCart } = useApiCart(cart);
  const isProcessingExternalOrder = useRef(false);

  let subjectName = '';
  let phoneNumber = '';
  let contactEmail = '';

  let userAddress = {
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipcode: '',
    deliveryInstructions: '',
  };

  if (cognitoUserInfo?.attributes) {
    subjectName = `${cognitoUserInfo.attributes.name} ${cognitoUserInfo.attributes.family_name}`;
    phoneNumber = cognitoUserInfo.attributes.phone_number;
    contactEmail = cognitoUserInfo.attributes.email;
    userAddress = {
      address1: cognitoUserInfo.attributes['custom:address_1'] || '',
      address2: cognitoUserInfo.attributes['custom:address_2'] || '',
      city: cognitoUserInfo.attributes['custom:city'] || '',
      state: cognitoUserInfo.attributes['custom:state'] || '',
      zipcode: cognitoUserInfo.attributes['custom:zipcode'] || '',
      deliveryInstructions: '',
    };
  }
  let defaultSubjects = [{ name: subjectName, dob: null, isAnAdult: false, phoneNumber: phoneNumber }];

  const getCartDataFromLocalStorage = () => {
    const savedCartDataJson = localStorage.getItem(CHECKOUT_CART_DATA_KEY);
    if (!savedCartDataJson) return null;

    return JSON.parse(savedCartDataJson);
  };

  let savedCartData = getCartDataFromLocalStorage();
  if (savedCartData) {
    if (savedCartData.contact) {
      contactEmail = savedCartData.contact;
    }
    if (savedCartData.subjects?.length > 0 && savedCartData.subjects[0].name) {
      defaultSubjects = savedCartData.subjects;
    }
    if (savedCartData.address?.address1 || savedCartData.address?.address2) {
      userAddress = savedCartData.address;
    }
  }

  const [subjects, setSubjects] = useState(defaultSubjects);
  const [contact, setContact] = useState(contactEmail);
  const [address, setAddress] = useState(userAddress);

  const [cartData, setCartData] = useState({
    contact: contact,
    quantity: quantity,
    subjects: subjects,
    address: address,
  });

  const getFirstAndLastName = (name) => {
    let nameWords = name.split(' ');
    let firstName = nameWords.shift();
    let lastName = '';
    if (nameWords && nameWords.length > 0) {
      while (nameWords.length > 0) {
        lastName += nameWords.shift() + ' ';
      }
      lastName = lastName.trimEnd();
    }
    return { firstName: firstName, lastName: lastName };
  };

  const getFormattedShopifyCheckoutUrl = (sCUrl, contact, subjects, address) => {
    if (!sCUrl.includes('?')) {
      sCUrl += '?';
    } else {
      sCUrl += '&';
    }

    const encodedEmailContact = encodeURIComponent(contact);
    if (contact && contact.length > 0) {
      sCUrl += 'checkout[email]=' + encodedEmailContact;
    } else {
      sCUrl += 'checkout[email]=';
    }

    const addAddressFieldToCheckoutUrl = (field, field_value) => {
      sCUrl += '&checkout[shipping_address][' + field + ']=' + field_value;
      sCUrl += '&checkout[billing_address][' + field + ']=' + field_value;
    };

    const { firstName, lastName } = getFirstAndLastName(subjects[0].name);
    addAddressFieldToCheckoutUrl('first_name', firstName);
    addAddressFieldToCheckoutUrl('last_name', lastName);

    addAddressFieldToCheckoutUrl('address1', address.address1);
    addAddressFieldToCheckoutUrl('address2', address.address2);
    addAddressFieldToCheckoutUrl('city', address.city);
    addAddressFieldToCheckoutUrl('province', address.state);
    addAddressFieldToCheckoutUrl('country', 'US');
    addAddressFieldToCheckoutUrl('zip', address.zipcode);

    if (subjects[0].phoneNumber) {
      addAddressFieldToCheckoutUrl('phone', subjects[0].phoneNumber);
    }

    return sCUrl;
  };

  // success and failure callbacks for go to shipping
  const setSuccessGoToShippingBtn = () => {
    setGoToShippingSubmitting(false);
    handleNext();
  };

  const setFailureGoToShippingBtn = () => {
    setGoToShippingEnabled(false);
    setGoToShippingSubmitting(false);
  };

  // success and failure callbacks for go to checkout
  const setSuccessGoToCheckoutBtn = () => {
    setGoToCheckoutEnabled(true);
    setGoToCheckoutSubmitting(false);
  };

  const setFailureGoToCheckoutBtn = () => {
    setGoToCheckoutEnabled(false);
    setGoToCheckoutSubmitting(false);
  };

  const handleApiCartBackendUpdate = async (sCUrl, newAddress, newContact, callerName) => {
    const cartData = {
      contact: newContact,
      quantity: quantity,
      subjects: subjects,
      address: newAddress,
    };

    // we can refactor this later on to use different versions, but essentially:
    // we are making a distinction between this method triggered on the first load
    // in the checkout(creating the cart and then this will be called as part of the useEffect)
    // and the other place we trigger it, is within the AddressCheckoutForm component
    // and callbacks(enabling the button) are only relevant for the AddressCheckoutForm component
    if (callerName === 'OrderPFAS') {
      await updateApiCart(cart, sCUrl, cartData);
    } else {
      await updateApiCartWithCallbacks(cart, sCUrl, cartData, setSuccessGoToCheckoutBtn, setFailureGoToCheckoutBtn);
    }
  };

  const handleApiCartAndBackendUpdates = async (newAddress, newContact, callerName = 'OrderPFAS') => {
    if (!cart || cart.length === 0) {
      return;
    }

    const { firstName, lastName } = getFirstAndLastName(subjects[0].name);

    let buyer = {
      countryCode: 'US',
      deliveryAddressPreferences: [
        {
          deliveryAddress: {
            address1: newAddress.address1,
            address2: newAddress.address2,
            city: newAddress.city,
            company: '',
            country: 'US',
            firstName: firstName,
            lastName: lastName,
            phone: subjects[0].phoneNumber,
            province: newAddress.state,
            zip: newAddress.zipcode,
          },
          deliveryAddressValidationStrategy: 'STRICT',
        },
      ],
      email: '',
      phone: subjects[0].phoneNumber,
    };

    await updateCartBuyer(cart, buyer);
    let sCUrl = await getCheckoutUrl(cart);
    if (!sCUrl) {
      return; // this happens when the cart has been checked out already (cart is deleted from shopify)
    }

    let updatedSCheckoutUrl = getFormattedShopifyCheckoutUrl(sCUrl, newContact, subjects, newAddress);
    setSCheckoutURL(updatedSCheckoutUrl);

    // We need to determine when to include the updatedSCheckoutUrl
    // as in the first render during the shop cart creation we know that address and subjects are not present
    let dynamicCheckoutURL = sCUrl;
    if (callerName !== 'OrderPFAS') dynamicCheckoutURL = updatedSCheckoutUrl;
    handleApiCartBackendUpdate(dynamicCheckoutURL, newAddress, newContact, callerName);
  };

  useEffect(() => {
    const fetchProductPrice = async () => {
      const sProduct = await getProduct(DEFAULT_PRODUCT_HANDLE);
      const sProductPrice = getProductPrice(sProduct);
      setProductPrice(sProductPrice);
      setProduct(sProduct);
    };

    const checkCartStatus = async (sCartId) => {
      const sCart = await getCart(sCartId);

      if (!sCart) {
        // If we don't get a cart, then it's prob paid already, check in our api shop cart to verify
        if (apiCart && apiCart.s_order_id) {
          // if we do have a shopify order, the order has been paid.
          let sOrderId = await getExternalOrder(sCartId);
          setOrder(sOrderId);
          setActiveStep(4);
          localStorage.removeItem(CHECKOUT_CART_KEY);
          localStorage.removeItem(CHECKOUT_CART_DATA_KEY);
        }
      }
    };

    const createNewCart = async () => {
      let newCartData = {
        contact: contact,
        quantity: quantity,
        subjects: subjects,
        address: address,
      };

      const sCartId = await createCart(newCartData);
      setCart(sCartId);
    };

    fetchProductPrice();

    if (cart) {
      checkCartStatus(cart); // Check if the cart has already been paid for
    } else {
      createNewCart();
    }
  }, [apiCart]);

  useEffect(() => {
    handleApiCartAndBackendUpdates(address, contact);
  }, [cart]);

  useEffect(() => {
    if (activeStep !== 3) {
      return;
    }

    let timeoutCheckExternalId;

    if (!sCheckoutURL) {
      // this happens when the cart has been checked out already (cart is deleted from shopify)
      setActiveStep(4);
      return;
    }

    const clearExistingTimeout = () => {
      if (timeoutCheckExternalId) {
        clearTimeout(timeoutCheckExternalId);
        timeoutCheckExternalId = null;
      }
    };

    const checkExternalOrder = async () => {
      if (isProcessingExternalOrder.current) {
        return;
      }

      clearExistingTimeout();

      try {
        isProcessingExternalOrder.current = true;
        // check for external order created, payment has been made
        let sOrderId = await getExternalOrder(cart);

        if (sOrderId) {
          // create internal order associated to external order
          let orderId = await handlePlaceOrder(cart, subjects, address);
          setActiveStep(4); // Order has been placed
          localStorage.removeItem(CHECKOUT_CART_KEY);
          localStorage.removeItem(CHECKOUT_CART_DATA_KEY);
          setOrder(orderId);
        } else {
          timeoutCheckExternalId = setTimeout(checkExternalOrder, 1000);
        }
      } catch (exception) {
        // wait a little longer before checking again
        timeoutCheckExternalId = setTimeout(checkExternalOrder, 2000);
      } finally {
        isProcessingExternalOrder.current = false;
      }
    };

    checkExternalOrder();

    // Cleanup function to clear the timeout if the component unmounts
    return () => {
      clearExistingTimeout();
    };
  }, [activeStep]);

  useEffect(() => {
    let newCartData = {
      contact: contact,
      quantity: quantity,
      subjects: subjects,
      address: address,
    };

    localStorage.setItem(CHECKOUT_CART_DATA_KEY, JSON.stringify(newCartData));
    setCartData(newCartData);
  }, [quantity, subjects, contact, address]);

  const updateQuantity = (newQuantity) => {
    setQuantity(newQuantity);

    if (subjects.length > newQuantity) {
      while (subjects.length > newQuantity) {
        subjects.pop();
      }
    } else if (subjects.length < newQuantity) {
      while (subjects.length < newQuantity) {
        subjects.push({ name: '', dob: null, isAnAdult: false, phoneNumber: null });
      }
    }

    const cartData = {
      contact: contact,
      quantity: newQuantity,
      subjects: subjects,
      address: address,
    };
    updateCartQuantity(cart, newQuantity, cartData);
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupMessage('');
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const selectQuantityAndHandleNext = () => {
    updateQuantity(1);
    handleNext();
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderStepWithStepper = (children) => {
    return (
      <>
        <OrderCheckoutStepperWrapper handleBack={handleBack} activeStep={activeStep} />
        {children}
      </>
    );
  };

  const renderSubjectsForm = () => {
    return renderStepWithStepper(
      <OrderSubjectsForm
        quantity={quantity}
        subjects={subjects}
        setSubjects={setSubjects}
        updateApiCartWithCallbacks={updateApiCartWithCallbacks}
        goToShippingSubmitting={goToShippingSubmitting}
        setGoToShippingSubmitting={setGoToShippingSubmitting}
        goToShippingEnabled={goToShippingEnabled}
        setGoToShippingEnabled={setGoToShippingEnabled}
        setSuccessGoToShippingBtn={setSuccessGoToShippingBtn}
        setFailureGoToShippingBtn={setFailureGoToShippingBtn}
        cart={cart}
      />,
    );
  };

  const openPaymentWindow = (sCUrl) => {
    window.open(sCUrl, '_blank', 'toolbar=no,scrollbars=yes,resizable=yes,top=50,left=50,width=600,height=800');
  };

  const renderCheckoutDeliveryForm = () => {
    return (
      <OrderCheckoutAddressContainer handleBack={handleBack} activeStep={activeStep}>
        <Grid
          item
          xs={12}
          md={12}
          sx={{
            padding: { sm: '0 14%', xs: '0', md: '0' },
          }}
        >
          <AddressCheckoutForm
            handleApiCartAndBackendUpdates={handleApiCartAndBackendUpdates}
            setGoToCheckoutSubmitting={setGoToCheckoutSubmitting}
            setGoToCheckoutEnabled={setGoToCheckoutEnabled}
            cartData={cartData}
            setAddress={setAddress}
            updateContact={(value) => setContact(value)}
            handleNextWizardStep={handleNext}
          />
        </Grid>

        <Box
          sx={{
            mt: 2,
            padding: { xs: '0', sm: '0 14%', md: '0' },
          }}
        >
          <PrimaryBlueButton
            onClick={() => {
              openPaymentWindow(sCheckoutURL);
              handleNext();
            }}
            buttonText="Go to Checkout"
            sxProps={{ mr: 1, width: { xs: '100%', md: 'auto' } }}
            additionalProps={{ disabled: !goToCheckoutEnabled }}
            isLoading={goToCheckoutSubmitting}
          />
        </Box>
      </OrderCheckoutAddressContainer>
    );
  };

  const renderHowManyTestsForm = () => {
    return <OrderCheckoutFirstStep selectQuantityAndHandleNext={selectQuantityAndHandleNext} />;
  };

  const renderThankYouPage = () => {
    return <OrderThankYouPage order={order} />;
  };

  const renderPaymentCheckout = () => {
    return (
      <CheckoutPaymentMethodContainer handleBack={handleBack} activeStep={activeStep}>
        <Grid
          item
          xs={12}
          md={12}
          sx={{
            padding: { sm: '0 14%', xs: '0', md: '0' },
          }}
        >
          <CheckoutPaymentMethod
            address={address}
            order={order}
            product={product}
            productPrice={productPrice}
            quantity={quantity}
            sCheckoutURL={sCheckoutURL}
            subjects={subjects}
            contact={contact}
          />
        </Grid>
      </CheckoutPaymentMethodContainer>
    );
  };

  const renderContentSteps = () => {
    return activeStep === 0 ? (
      renderHowManyTestsForm()
    ) : activeStep === 1 ? (
      renderSubjectsForm()
    ) : activeStep === 2 ? (
      renderCheckoutDeliveryForm()
    ) : activeStep === 3 ? (
      renderPaymentCheckout()
    ) : activeStep === 4 ? (
      renderThankYouPage()
    ) : (
      <>Something unexpected happened. Please try again.</>
    );
  };

  return (
    <Container
      id="orderpfas-home"
      sx={{
        marginTop: { xs: '8px', md: '16px' },
        backgroundColor: theme.custom.orderPFAS.container.backgroundColor,
        borderRadius: '16px',
        padding: '16px',
      }}
    >
      {renderContentSteps()}

      {showPopup && (
        <div className="popup">
          <div className="popup-content">
            <p>{popupMessage}</p>
            <button onClick={closePopup}>Close</button>
          </div>
        </div>
      )}
    </Container>
  );
}
